import { useState } from 'react';
import { Check, Filter, RefreshCw, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchInput } from '@/components/custom/search-input';
import {
  hasAnyActiveValue,
  type FilterState,
  type FilterDefinition,
} from '@/components/filter-bar/filter-bar';
import { TaskFilterBar } from './TaskFilterBar';
import type { TaskScope } from './task-filter-utils';

/**
 * Single-row toolbar for the task inbox column. ~48px tall.
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ My 12  All 47  Un 8 │                       🔍  ☰  ↻   │
 *   └──────────────────────────────────────────────────────────┘
 *
 * - Tabs on the left drive the active scope.
 * - Search icon expands a full-width search input that overlays the tabs.
 * - Filter icon opens a popover with the multi-select FilterBar inside —
 *   keeps the toolbar single-row no matter how many filters are active.
 *   A small primary dot on the filter icon indicates active filters.
 * - Refresh stays as the rightmost icon when not in search mode.
 */
interface Props {
  scope: TaskScope;
  scopeCounts: { my: number; all: number; unassigned: number };
  onScopeChange: (scope: TaskScope) => void;

  searchTerm: string;
  onSearchChange: (value: string) => void;

  filterState: FilterState;
  onFilterChange: (state: FilterState) => void;
  filterDefinitions: FilterDefinition[];

  onRefresh: () => void;
  isFetching: boolean;

  /**
   * Tri-state inputs for the leading "Select all on page" checkbox.
   * Same control that used to live inside the action bar — promoted
   * to the toolbar so the affordance is discoverable BEFORE any row
   * is checked, and so we don't render two near-identical checkboxes
   * (one per band) once selection starts.
   */
  selectedCount: number;
  idsOnPageCount: number;
  allOnPageSelected: boolean;
  onToggleSelectAllOnPage: () => void;
}

const SCOPE_TABS: { value: TaskScope; label: string; title: string }[] = [
  { value: 'my', label: 'My', title: 'My tasks' },
  { value: 'all', label: 'All', title: 'All tasks' },
  { value: 'unassigned', label: 'Un', title: 'Unassigned tasks' },
];

export function TaskListHeader({
  scope,
  scopeCounts,
  onScopeChange,
  searchTerm,
  onSearchChange,
  filterState,
  onFilterChange,
  filterDefinitions,
  onRefresh,
  isFetching,
  selectedCount,
  idsOnPageCount,
  allOnPageSelected,
  onToggleSelectAllOnPage,
}: Props): React.ReactElement {
  // Default search-open when the user already has a query so a returning
  // user immediately sees their term — otherwise collapsed.
  const [searchOpen, setSearchOpen] = useState(Boolean(searchTerm));
  const [filterOpen, setFilterOpen] = useState(false);
  const hasActiveFilters = hasAnyActiveValue(filterState);

  return (
    <div className="flex min-h-[3rem] items-center gap-3 border-b px-3 py-2">
      {searchOpen ? (
        <SearchInput
          value={searchTerm}
          onValueChange={onSearchChange}
          placeholder="Search by name, email, phone..."
          className="min-w-0 flex-1"
          autoFocus
        />
      ) : (
        <>
          {/* Tri-state "select all on page" checkbox. Sits before the
              tabs so the bulk-select affordance is discoverable with
              zero rows selected — clicking it from empty state selects
              every visible row in one shot.
                - empty (no rows selected) → click selects all on page
                - dash  (some-but-not-all) → click expands to all
                - filled (all on page)     → click clears
              Disabled when the page has no rows. Same control that
              used to live inside the action bar — promoting it
              eliminates the duplicate-checkbox UX. */}
          {(() => {
            const empty = selectedCount === 0;
            const partial = selectedCount > 0 && !allOnPageSelected;
            const isDisabled = idsOnPageCount === 0;
            const ariaChecked = allOnPageSelected ? true : partial ? 'mixed' : false;
            const label = allOnPageSelected
              ? 'Clear selection'
              : partial
                ? 'Select all on page'
                : 'Select all tasks on page';
            return (
              <button
                type="button"
                role="checkbox"
                aria-checked={ariaChecked}
                aria-label={label}
                title={label}
                disabled={isDisabled}
                onClick={onToggleSelectAllOnPage}
                className={cn(
                  'inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
                  allOnPageSelected
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background text-muted-foreground hover:border-foreground'
                )}
              >
                {allOnPageSelected ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : partial ? (
                  <span className="block h-0.5 w-2 bg-current" />
                ) : (
                  // Empty state — no glyph, just an outlined box. The
                  // label-only affordance still reads as "click me to
                  // select everything" because it sits before the tabs.
                  !empty && null
                )}
              </button>
            );
          })()}
          {/* role="tablist" + role="tab" + aria-selected so screen readers
           * announce these as a tab interface, not a row of buttons. */}
          <div role="tablist" aria-label="Task scope" className="flex shrink-0 items-center gap-3">
            {SCOPE_TABS.map((t) => {
              const selected = scope === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => onScopeChange(t.value)}
                  title={t.title}
                  aria-label={t.title}
                  className={cn(
                    'flex items-center gap-1.5 border-b-2 pb-1 text-sm transition-colors',
                    selected
                      ? 'border-foreground font-semibold text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <span>{t.label}</span>
                  <span className="text-xs text-muted-foreground">{scopeCounts[t.value]}</span>
                </button>
              );
            })}
          </div>
          <div className="min-w-0 flex-1" />
        </>
      )}

      {/* Right cluster — fixed order: search → filter → refresh.
       * In search mode only the close (X) is visible so the input can
       * span the full row. */}
      <div className="flex shrink-0 items-center gap-0.5">
        {searchOpen ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setSearchOpen(false)}
            aria-label="Hide search"
            title="Hide search"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSearchOpen(true)}
              aria-label="Show search"
              title="Search"
            >
              <Search className="h-3.5 w-3.5" />
            </Button>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', filterOpen && 'text-primary')}
                  aria-label={hasActiveFilters ? 'Edit filters' : 'Filters'}
                  title="Filters"
                >
                  <div className="relative">
                    <Filter className="h-3.5 w-3.5" />
                    {hasActiveFilters && (
                      <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />
                    )}
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[22rem] p-0">
                <div className="flex items-center justify-between border-b px-3 py-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Filters
                  </span>
                  {hasActiveFilters && (
                    <Button
                      variant="link"
                      size="xs"
                      className="h-auto p-0 text-xs"
                      onClick={() => onFilterChange({})}
                    >
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="p-3">
                  <TaskFilterBar
                    filterState={filterState}
                    onFilterChange={onFilterChange}
                    definitions={filterDefinitions}
                  />
                </div>
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRefresh}
              disabled={isFetching}
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
